/**
 * Shared metadata schema definitions for protocol steps.
 *
 * This file is the single source of truth for:
 * - Which fields belong to step base interfaces vs metadata
 * - Which metadata fields are allowed for each step kind
 * - Compile-time type assertions ensuring Step and StepDefinition metadata stay in sync
 */

import {IProtocolStepKind} from "./stepKind";

/**
 * Fields that belong to IProtocolStepDefinitionBase (server-side "protocol_step_definitions" table).
 * These are excluded when extracting metadata for step definitions.
 */
export const stepDefinitionBaseFields = [
    "id",
    "protocolId",
    "kind",
    "name",
    "instructions",
    "createdAt",
] as const;

export type TStepDefinitionBaseField = typeof stepDefinitionBaseFields[number];

/**
 * Fields that belong to IProtocolStepBase (server-side "protocol_steps" table).
 * These are excluded when extracting metadata for protocol steps.
 * Includes all stepDefinitionBaseFields plus step-specific fields.
 */
export const stepBaseFields = [
    ...stepDefinitionBaseFields,
    "status",
    "startTime",
    "endTime",
    "transient",
] as const;

export type TStepBaseField = typeof stepBaseFields[number];

/**
 * Metadata fields allowed for each step kind.
 * This is the single source of truth - both IProtocolStep and IProtocolStepDefinition
 * variants must use these same fields.
 */
export const stepMetadataFields: Record<IProtocolStepKind, readonly string[]> = {
    [IProtocolStepKind.MEG_SCAN]: ["tags", "duration"] as const,
    [IProtocolStepKind.EEG_SCAN]: ["tags", "duration"] as const,
    [IProtocolStepKind.ECG_SCAN]: ["tags", "duration"] as const,
    [IProtocolStepKind.MEASURE_BP]: ["count", "readings"] as const,
    [IProtocolStepKind.MEASURE_PLR]: ["count", "readings"] as const,
    [IProtocolStepKind.MEASURE_HR]: ["bpm"] as const,
    [IProtocolStepKind.STIM_EVENT]: ["stimMetadata", "tags"] as const,
    [IProtocolStepKind.Q_AND_A]: ["question", "answer"] as const,
    [IProtocolStepKind.QUESTIONNAIRE]: ["items"] as const,
    [IProtocolStepKind.NOTE]: ["note"] as const,
    [IProtocolStepKind.REST_EVENT]: ["duration"] as const,
    [IProtocolStepKind.START_DATA_COLLECTION]: ["data_dir_path", "dir_configs", "aria"] as const,
    [IProtocolStepKind.STOP_DATA_COLLECTION]: [] as const,
    [IProtocolStepKind.GROUP]: ["memberStepIds"] as const,
    [IProtocolStepKind.INSTRUCTIONS]: [] as const,
    [IProtocolStepKind.VAS_PAIN_SCORE]: ["score"] as const,
    [IProtocolStepKind.NRS_PAIN_SCORE]: ["score"] as const,
    [IProtocolStepKind.PP_RELIEF]: ["score"] as const,
    [IProtocolStepKind.PORT_CONFIG]: ["count", "ports"] as const,
};

/**
 * Get the allowed metadata field names for a step kind.
 */
export const getMetadataFieldsForKind = (kind: IProtocolStepKind): readonly string[] => {
    return stepMetadataFields[kind] ?? [];
};

/**
 * Check if a field name is a base field (not metadata).
 */
export const isStepBaseField = (field: string): field is TStepBaseField => {
    return (stepBaseFields as readonly string[]).includes(field);
};

/**
 * Check if a field name is a definition base field (not metadata).
 */
export const isStepDefinitionBaseField = (field: string): field is TStepDefinitionBaseField => {
    return (stepDefinitionBaseFields as readonly string[]).includes(field);
};

// ============================================================================
// Compile-time type assertions
// ============================================================================

/**
 * Utility type to extract keys from a type as a union of string literals.
 */
type KeysOf<T> = keyof T & string;

/**
 * Type assertion: Ensures that the metadata fields for a Step and StepDefinition
 * variant are identical. If they diverge, this will produce a compile error.
 *
 * Usage: Add assertions below for each step kind pair.
 */
type AssertSameMetadata<
    TStep,
    TStepDef,
    TStepBase,
    TStepDefBase
> = KeysOf<Omit<TStep, keyof TStepBase>> extends KeysOf<Omit<TStepDef, keyof TStepDefBase>>
    ? KeysOf<Omit<TStepDef, keyof TStepDefBase>> extends KeysOf<Omit<TStep, keyof TStepBase>>
        ? true
        : false
    : false;

// Import types for compile-time assertions (these create no runtime overhead)
import type {
    IMegScanStep,
    IEEGScanStep,
    IECGScanStep,
    IMeasureBloodPressureStep,
    IMeasurePLRStep,
    IMeasureHeartRateStep,
    IStimStep,
    IQuestionAnswerMetricStep,
    IQuestionnaireStep,
    INoteStep,
    IRestStep,
    IStartDataCollectionStep,
    IStopDataCollectionStep,
    IGroupStep,
    IProtocolStepBase, IInstructionStep, IVasPainScoreStep, INRSPainScoreStep, IPPReliefStep, IPortConfigStep,
} from "./protocol";

import type {
    IMegScanStepDefinition,
    IEEGScanStepDefinition,
    IECGScanStepDefinition,
    IMeasureBloodPressureStepDefinition,
    IMeasurePLRStepDefinition,
    IMeasureHeartRateStepDefinition,
    IStimStepDefinition,
    IQuestionAnswerMetricStepDefinition,
    IQuestionnaireStepDefinition,
    INoteStepDefinition,
    IRestStepDefinition,
    IStartDataCollectionStepDefinition,
    IStopDataCollectionStepDefinition,
    IGroupStepDefinition,
    IProtocolStepDefinitionBase, IInstructionStepDefinition, IVasPainScoreStepDefinition, INRSPainScoreStepDefinition, IPPReliefStepDefinition, IPortConfigStepDefinition,
} from "./protocolDefinitions";

/**
 * Compile-time assertions - these will cause TypeScript errors if metadata fields diverge
 * between Step and StepDefinition variants.
 *
 * If you see a type error like "Type 'false' is not assignable to type 'true'", it means
 * the metadata fields for that step type don't match between the Step and StepDefinition.
 */
export type StepMetadataAssertions = {
    megScan: AssertSameMetadata<IMegScanStep, IMegScanStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    eegScan: AssertSameMetadata<IEEGScanStep, IEEGScanStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    ecgScan: AssertSameMetadata<IECGScanStep, IECGScanStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    measureBP: AssertSameMetadata<IMeasureBloodPressureStep, IMeasureBloodPressureStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    measurePLR: AssertSameMetadata<IMeasurePLRStep, IMeasurePLRStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    measureHR: AssertSameMetadata<IMeasureHeartRateStep, IMeasureHeartRateStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    stim: AssertSameMetadata<IStimStep, IStimStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    qa: AssertSameMetadata<IQuestionAnswerMetricStep, IQuestionAnswerMetricStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    questionnaire: AssertSameMetadata<IQuestionnaireStep, IQuestionnaireStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    note: AssertSameMetadata<INoteStep, INoteStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    rest: AssertSameMetadata<IRestStep, IRestStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    startDataCollection: AssertSameMetadata<IStartDataCollectionStep, IStartDataCollectionStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    stopDataCollection: AssertSameMetadata<IStopDataCollectionStep, IStopDataCollectionStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    group: AssertSameMetadata<IGroupStep, IGroupStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    instructions: AssertSameMetadata<IInstructionStep, IInstructionStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    vasPainScore: AssertSameMetadata<IVasPainScoreStep, IVasPainScoreStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    nrsPainScore: AssertSameMetadata<INRSPainScoreStep, INRSPainScoreStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    ppRelief: AssertSameMetadata<IPPReliefStep, IPPReliefStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
    portConfig: AssertSameMetadata<IPortConfigStep, IPortConfigStepDefinition, IProtocolStepBase, IProtocolStepDefinitionBase>;
};

// This assertion ensures all metadata assertions are true at compile time
// If any step type has mismatched metadata, this will fail to compile
export type AssertAllMetadataValid = StepMetadataAssertions[keyof StepMetadataAssertions] extends true ? true : never;

